import { useMemo, useState } from 'react';
import { findDisjointMelds, isWild, jokerContext, partitionIntoMelds, sortHand, type MarriageLegal, type MarriageView } from '@cardhub/engine';
import { PlayingCard, shortCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Coins, Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { toast } from '../store/ui';
import { MySeat, SeatView, useNarrow, useSpeech } from './common';

/** Groups detected melds first so players can see their progress (pure melds before show, any after). */
function arrange(hand: string[], view: MarriageView, hasShown: boolean): { groups: string[][]; rest: string[] } {
  const ctx = hasShown && view.tiplu ? jokerContext({ tiplu: view.tiplu, config: view.config }) : null;
  for (let n = hasShown ? 4 : 3; n >= 1; n--) {
    const melds = findDisjointMelds(hand, n, ctx, !hasShown);
    if (melds) {
      const used = new Set(melds.flat());
      return { groups: melds.map((m) => m.map((i) => hand[i]!)), rest: sortHand(hand.filter((_, i) => !used.has(i))) };
    }
  }
  return { groups: [], rest: sortHand(hand) };
}

export function MarriageTable() {
  const t = useT();
  const view = useGame((s) => s.view) as MarriageView;
  const legal = useGame((s) => s.legal) as MarriageLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const [auto, setAuto] = useState(true);
  const [confirm, setConfirm] = useState<{ kind: 'show' | 'finish'; groups: string[][]; discard?: string } | null>(null);
  const total = view.players.length;
  const me = mySeat !== null ? view.players[mySeat] : null;
  const hasShown = Boolean(me?.hasShown);
  const ctx = view.tiplu ? jokerContext({ tiplu: view.tiplu, config: view.config }) : null;
  const layout = useMemo(() => (auto ? arrange(view.hand, view, hasShown) : { groups: [], rest: sortHand(view.hand) }), [view, hasShown, auto]);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const narrow = useNarrow();
  // Big hands (21–23 cards) go in two rows so each card keeps a comfortable tap target.
  const twoRows = view.hand.length > 12;
  const size = twoRows || narrow ? 'md' : 'lg';
  const pileSize = narrow ? 'md' : 'lg';

  const doShow = () => {
    const melds = findDisjointMelds(view.hand, 3, null, true);
    if (!melds) {
      toast(t('mr.selectShow'), 'error');
      return;
    }
    setConfirm({ kind: 'show', groups: melds.map((m) => m.map((i) => view.hand[i]!)) });
  };
  const doFinish = () => {
    const order = selected ? [selected, ...view.hand.filter((c) => c !== selected)] : view.hand;
    for (const discard of order) {
      if (discard === legal?.lockedCard) continue;
      const rest = view.hand.filter((c) => c !== discard);
      const groups = partitionIntoMelds(rest, ctx);
      if (groups) {
        setConfirm({ kind: 'finish', groups, discard });
        return;
      }
    }
    toast('Your cards do not form complete melds yet', 'error');
  };

  const cards = [
    ...layout.groups.flatMap((g, gi) =>
      g.map((c, k) => (
        <span key={c} style={{ display: 'inline-block', marginRight: k === g.length - 1 ? 10 : 0 }} data-group={gi}>
          <PlayingCard id={c} size={size} wild={hasShown && isWild(c, ctx)} selected={selected === c} onClick={() => legal?.canDiscard && setSelected(selected === c ? null : c)} />
        </span>
      )),
    ),
    ...layout.rest.map((c) => (
      <span key={c} style={{ display: 'inline-block' }}>
        <PlayingCard id={c} size={size} wild={hasShown && isWild(c, ctx)} selected={selected === c} unplayable={c === legal?.lockedCard && legal?.canDiscard} onClick={() => legal?.canDiscard && setSelected(selected === c ? null : c)} />
      </span>
    )),
  ];
  const split = twoRows ? Math.ceil(cards.length / 2) : cards.length;

  return (
    <>
      <div className="stage">
      <div className="felt" />
      {view.players.map((p, s) => (
        <SeatView
          key={s}
          seat={s}
          info={seats[s]}
          mySeat={mySeat}
          total={total}
          turn={turn}
          speech={speech[s]}
          folded={p ? !p.inHand && view.phase !== 'WAITING' : false}
          sub={p ? <Coins amount={p.chips} size={14} /> : null}
          tags={
            p && (
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {p.inHand && <span className="tag">{p.handCount} cards</span>}
                {p.hasShown && <span className="tag seen">SHOWN</span>}
              </>
            )
          }
        >
          {p && p.shown.length > 0 && (
            <div className="melds">
              {p.shown.slice(0, 3).map((g, i) => (
                <div key={i} className="meld">
                  {g.map((c) => (
                    <PlayingCard key={c} id={c} size="sm" />
                  ))}
                </div>
              ))}
            </div>
          )}
        </SeatView>
      ))}
      <div className="felt-center centered">
        {view.phase === 'WAITING' ? (
          <div className="center-note">{t('table.waitingPlayers')}</div>
        ) : view.phase === 'HAND_END' && view.lastResult ? (
          <div className="card glass" style={{ textAlign: 'center', maxHeight: '48vh', overflow: 'auto' }}>
            <strong>{view.lastResult.winner !== null ? `🏆 ${name(view.lastResult.winner)}` : 'Round void'}</strong>
            <div className="dim" style={{ fontSize: 'var(--fs-xs)', marginBottom: 6 }}>{t('mr.tiplu', { card: shortCard(view.lastResult.tiplu) })}</div>
            <table className="scoreboard">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Maal</th>
                  <th>Points</th>
                  <th>Coins</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(view.lastResult.points).map((k) => {
                  const s = Number(k);
                  return (
                    <tr key={s}>
                      <td className="ellipsis">{name(s)}</td>
                      <td className="num">{view.lastResult!.maal[s]}</td>
                      <td className="num">{view.lastResult!.points[s]}</td>
                      <td className="num" style={{ color: (view.lastResult!.chips[s] ?? 0) < 0 ? 'var(--danger)' : 'var(--success)' }}>{view.lastResult!.chips[s]}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <div className="row" style={{ gap: 18 }}>
              <button className="col" style={{ alignItems: 'center', gap: 4, background: 'none', border: 0 }} disabled={!legal?.canDrawStock || acting} onClick={() => void act({ type: 'draw', from: 'stock' })} aria-label={t('mr.drawStock')}>
                <PlayingCard faceDown size={pileSize} className={legal?.canDrawStock ? 'playable' : ''} />
                <span className="center-note">{t('mr.stock', { n: view.stockCount })}</span>
              </button>
              <button className="col" style={{ alignItems: 'center', gap: 4, background: 'none', border: 0 }} disabled={!legal?.canDrawDiscard || acting} onClick={() => void act({ type: 'draw', from: 'discard' })} aria-label={t('mr.pickDiscard')}>
                {view.discardTop ? <PlayingCard id={view.discardTop} size={pileSize} className={legal?.canDrawDiscard ? 'playable' : ''} /> : <span className={`pcard ${pileSize}`} />}
                <span className="center-note">{t('mr.pickDiscard')}</span>
              </button>
              <div className="col" style={{ alignItems: 'center', gap: 4 }}>
                {view.tiplu ? <PlayingCard id={view.tiplu} size={narrow ? 'sm' : 'md'} wild /> : <PlayingCard faceDown size={narrow ? 'sm' : 'md'} />}
                <span className="center-note" style={{ fontSize: 'var(--fs-xs)' }}>{view.tiplu ? 'Tiplu' : 'Joker'}</span>
              </div>
            </div>
            {!view.tiplu && <div className="center-note" style={{ fontSize: 'var(--fs-xs)' }}>{t('mr.hiddenJoker')}</div>}
          </>
        )}
      </div>
      <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={me ? <Coins amount={me.chips} size={14} /> : null} />
      </div>
      <div className="table-bottom">
        {me && me.shown.length > 0 && (
          <div className="melds" style={{ padding: '2px 0' }}>
            {me.shown.map((g, i) => (
              <div key={i} className="meld">
                {g.map((c) => (
                  <PlayingCard key={c} id={c} size="sm" />
                ))}
              </div>
            ))}
          </div>
        )}
        <div role="group" aria-label="Your hand" className="hand-rows">
          <Hand size={size} label={twoRows ? 'Your hand, row 1' : 'Your hand'} maxStep={0.6}>
            {cards.slice(0, split)}
          </Hand>
          {twoRows && (
            <Hand size={size} label="Your hand, row 2" maxStep={0.6}>
              {cards.slice(split)}
            </Hand>
          )}
        </div>
        <div className="action-bar">
          <button className="btn ghost sm" onClick={() => setAuto(!auto)} aria-pressed={auto}>
            {t('mr.autoArrange')}
          </button>
          {legal?.canDiscard && (
            <button
              className="btn primary"
              disabled={!selected || selected === legal.lockedCard || acting}
              onClick={() => {
                if (selected) void act({ type: 'discard', card: selected }).then((ok) => ok && setSelected(null));
              }}
            >
              {t('mr.discard')}
              {selected ? ` ${shortCard(selected)}` : ''}
            </button>
          )}
          {legal?.canShow && (
            <button className="btn gold" disabled={acting} onClick={doShow}>
              {t('mr.show')}
            </button>
          )}
          {legal?.canFinish && (
            <button className="btn gold" disabled={acting} onClick={doFinish}>
              {t('mr.finish')}
            </button>
          )}
        </div>
      </div>
      <Modal open={Boolean(confirm)} onClose={() => setConfirm(null)} title={confirm?.kind === 'show' ? t('mr.show') : t('mr.finish')}>
        {confirm && (
          <>
            <div className="melds" style={{ marginBottom: 12 }}>
              {confirm.groups.map((g, i) => (
                <div key={i} className="meld">
                  {g.map((c) => (
                    <PlayingCard key={c} id={c} size="md" wild={confirm.kind === 'finish' && isWild(c, ctx)} />
                  ))}
                </div>
              ))}
            </div>
            {confirm.discard && (
              <p className="dim">
                Discard: <strong>{shortCard(confirm.discard)}</strong>
              </p>
            )}
            <div className="row">
              <button className="btn ghost grow" onClick={() => setConfirm(null)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn primary grow"
                onClick={() => {
                  const c = confirm;
                  setConfirm(null);
                  if (c.kind === 'show') void act({ type: 'show', groups: c.groups });
                  else void act({ type: 'finish', discard: c.discard, groups: c.groups });
                }}
              >
                {t('common.continue')}
              </button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
